import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronDown, Loader2, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    document.title = "FAQs - BODYCRUSH";
    async function loadFaqs() {
      try {
        const res = await api.get("/faqs");
        setFaqs(res.data);
      } catch (err) {
        console.error("Failed to load FAQs:", err);
      } finally {
        setLoading(false);
      }
    }
    loadFaqs();
  }, []);

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const cat = faq.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">
          Home
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-foreground">FAQs</span>
      </nav>

      {/* Header */}
      <div className="text-center mb-12">
        <HelpCircle className="w-12 h-12 text-primary mx-auto mb-4" />
        <h1 className="text-3xl font-bold mb-3">Frequently Asked Questions</h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Find answers to common questions about our products, shipping, returns, and more.
        </p>
      </div>

      {faqs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No FAQs available yet.
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold mb-4 text-primary">{category}</h2>
              <div className="space-y-2">
                {categoryFaqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="border border-border rounded-xl overflow-hidden bg-card"
                  >
                    <button
                      onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-secondary/30 transition-colors"
                    >
                      <span className="font-medium">{faq.question}</span>
                      <ChevronDown
                        className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform ${
                          expandedId === faq.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {expandedId === faq.id && (
                      <div className="px-5 pb-4 pt-1 text-muted-foreground border-t border-border bg-secondary/10">
                        <p className="whitespace-pre-wrap">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contact CTA */}
      <div className="mt-12 pt-8 border-t border-border text-center">
        <p className="text-muted-foreground mb-4">
          Can't find what you're looking for?
        </p>
        <Link
          to="/policies/contact-information"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
        >
          Contact Us
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
